/*
 * FLOAT1 wizard input component
 * <p>
 *
 * @author lachezar.parvin
 * @author Gallagher Bassett IT
 * @author Copyright (c) 2020 Gallagher Bassett Services, Inc.
 *
 */

import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  Renderer2,
  ViewChild
} from '@angular/core';

import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';

@Component({
    selector: 'reporter-float1',
    templateUrl: './float1.component.html',
    styleUrls: ['./float1.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => Float1Component),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => Float1Component),
            multi: true
        },
    ],
    standalone: false
})

export class Float1Component implements ControlValueAccessor, Validator, AfterViewInit {

  constructor(private renderer: Renderer2, private elementRef: ElementRef) {
  }

  set value(v: any) {
    if (v !== this.innerValue) {
      this.innerValue = v;
      this.onChange(v);
    }
  }

  get value() {
    return this.innerValue;
  }

  get required(): boolean {
    return this.elementRef.nativeElement.attributes.hasOwnProperty('required');
  }

  innerValue: any;
  initialized = false;
  initialValue: any;

  @ViewChild('field', { static: true }) field: ElementRef;
  onChange = (_: any) => {};
  onTouched = () => {};

  ngAfterViewInit() {
    this.initialValue = this.value;
    if (this.required) {
      this.renderer.setAttribute(this.field.nativeElement, 'required', '');
    }
    if (this.elementRef.nativeElement.classList.contains('required')) {
      this.renderer.addClass(this.field.nativeElement, 'required');
    }
    this.initialized = true;
  }

  writeValue(value: any): void {
    let newVal;
    if (value === null || value === undefined) {
      newVal = undefined;
    } else if (`${ value }`.match(/\./g)) {
      newVal = parseFloat(value).toFixed(1);
    } else {
      newVal = parseInt(value, 10);
    }

    this.innerValue = newVal;
    this.renderer.setProperty(this.elementRef.nativeElement, 'value', newVal);
    if (this.field) {
      this.renderer.setProperty(this.field.nativeElement, 'value', newVal);
    }

    this.onChange(newVal);
    this.onTouched();
  }

  registerOnChange(fn: (value: any) => any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
  }

  validate(control: FormControl): ValidationErrors {
    if (!this.initialized) {
      return null;
    }

    if (this.required && (this.value === null || this.value === undefined)) {
      return {
        required: true,
        message: ' is required.'
      };
    }

    if (this.value !== null && this.value !== undefined && control.value !== this.initialValue) {
      if (this.value < -999999999.9) {
        return {
          rangeUnderflow: true,
          message: ' must be greater than -999999999.9.'
        };
      }
      if (this.value > 999999999.9) {
        return {
          rangeOverflow: true,
          message: ' must be less than 999999999.9.'
        };
      }
      if (isNaN(control.value)) {
        return {
          badInput: true,
          message: ' must be a correct decimal number.'
        };
      }
    }
    return null;
  }
}
